import { t } from '@exam-prep/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { AnswerQueue, clientNow, TimeTracker } from '../model/offline';
import { guardMock } from '../../../lib/tma';
import type { AttemptView } from '../../../lib/api-types';
import { isManaged, type Local } from '../model/palette';

/**
 * Loads the attempt, keeps the server clock offset, saves answers through the offline queue and
 * resyncs every minute. The client timer is display only (spec §3.4); the server decides.
 */
export function usePlayerSession(attemptId: string, onDone: (managed: boolean) => void) {
  const [view, setView] = useState<AttemptView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [idx, setIdx] = useState(0);
  const [local, setLocal] = useState<Record<string, Local>>({});
  const [offset, setOffset] = useState(0);
  const [now, setNow] = useState(clientNow);
  const [waiting, setWaiting] = useState(0);
  const [offline, setOffline] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [queue] = useState(() => new AnswerQueue(attemptId));
  const [timer] = useState(() => new TimeTracker());
  const unitRef = useRef<number | null>(null);
  const managedRef = useRef(false);

  /** Show question `at` of the open part; the first visit marks it "not answered". */
  const visit = useCallback(
    (v: AttemptView, at: number) => {
      const open = v.sections.filter((s) => s.state === 'open').flatMap((s) => s.questions);
      const q = open[at];
      setIdx(at);
      if (!q) return;
      timer.show(q.itemId);
      setLocal((l) => {
        if (l[q.itemId] || q.choice !== null || q.marked) return l;
        queue.put({ itemId: q.itemId, choice: null, marked: false, timeMs: 0 });
        return { ...l, [q.itemId]: { choice: null, marked: false } };
      });
    },
    [queue, timer],
  );

  const load = useCallback(async () => {
    try {
      const v = await api<AttemptView>('GET', `/app/attempts/${attemptId}`);
      setOffset(Date.parse(v.serverTime) - clientNow());
      const first = unitRef.current === null;
      const moved = !first && v.unitIdx !== unitRef.current;
      if (moved) setNotice(t('miniapp.player.locked'));
      unitRef.current = v.unitIdx;
      setView(v);
      managedRef.current = isManaged(v);
      if (v.status !== 'in_progress') onDone(managedRef.current);
      else if (first || moved) visit(v, 0);
    } catch (e) {
      setError(e);
    }
  }, [attemptId, onDone, visit]);

  useEffect(() => {
    queue.listen(
      (r, n) => {
        setOffline(false);
        setWaiting(n);
        setOffset(Date.parse(r.serverTime) - clientNow());
        if (r.status !== 'in_progress' || r.unitIdx !== unitRef.current) void load();
      },
      (n) => {
        setOffline(true);
        setWaiting(n);
      },
    );
    guardMock(true);
    // Initial load and replay of answers saved while offline (async; state is set after fetch).
    const boot = setTimeout(() => void queue.flush().then(load), 0);
    const tick = setInterval(() => setNow(clientNow()), 1000);
    // Resync with the server clock every 60 s (spec §3.4: the client timer is display only).
    const sync = setInterval(() => void queue.flush().then(load), 60_000);
    const online = () => void queue.flush();
    window.addEventListener('online', online);
    return () => {
      timer.leave();
      guardMock(false);
      clearTimeout(boot);
      clearInterval(tick);
      clearInterval(sync);
      window.removeEventListener('online', online);
    };
  }, [load, queue, timer]);

  return {
    view,
    error,
    setError,
    idx,
    local,
    setLocal,
    offset,
    now,
    waiting,
    offline,
    notice,
    setNotice,
    queue,
    timer,
    visit,
    load,
    managed: isManaged(view),
  };
}
